import {DomainGraphType} from 'app-core/framework/CoreObjectConstants';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import * as SpreadsheetBulkImportConstants from 'bulk-import/SpreadsheetBulkImportConstants';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  concentrationUnitTypePredicate,
  containerQuantityUnitTypePredicate,
} from 'inventory/InventoryUnitUtil';
import {notebookTableInventoryShortcutsActionFunction} from 'inventory/notebookCustomInventoryActionFunctions';
import {AddStructuredTableRowsUsingAIResult} from 'notebook/ai-tools/flexible-data-entry/addStructuredTableRowsUsingAI';
import {ClientId} from 'notebook/ELNConstants';
import * as ContainerSourceTypeCustomEval from 'notebook/integrations/samples/ContainerSourceTypeCustomEval';
import {
  VALID_CONCENTRATION_UNITS,
  VALID_CONTAINER_QUANITY_UNITS,
  VALID_VOLUME_UNITS,
} from 'notebook/integrations/samples/DeprecatedUnitSelectorConstants';
import {
  InventoryStructuredTableSchemaType,
  InventoryTableMetadata,
} from 'notebook/integrations/samples/InventoryTableConstants';
import {InventoryConcentrationCustomEval} from 'notebook/integrations/samples/transfer-table-columns/InventoryTableConcentrationCustomEval';
import {InventoryQuantityCustomEval} from 'notebook/integrations/samples/transfer-table-columns/InventoryTableQuantityCustomEval';
import * as ParentStorableCustomEval from 'notebook/integrations/samples/transfer-table-columns/ParentStorableCustomEval';
import * as PlateSourceTypeCustomEval from 'notebook/integrations/samples/transfer-table-columns/PlateSourceTypeCustomEval';
import * as SchemaTagsCustomEval from 'notebook/integrations/samples/transfer-table-columns/SchemaTagsCustomEval';
import {
  extractContainerSchemaAndColumnGroup,
  extractPlateSchemaAndColumnGroup,
} from 'notebook/integrations/samples/TransferStructuredTableSchemasUtil';
import addSamplesFromBarcodeScanner from 'notebook/sample-propagation/addSamplesFromBarcodeScanner';
import addSamplesFromBoxOrPlate from 'notebook/sample-propagation/addSamplesFromBoxOrPlate';
import addSamplesFromStructuredTable from 'notebook/sample-propagation/addSamplesFromStructuredTable';
import addSamplesFromWorklist from 'notebook/sample-propagation/addSamplesFromWorklist';
import {
  AddSamplesToTableTrackingParams,
  CompleteAddSamplesTrackingParams,
  trackAddSamples,
} from 'notebook/sample-propagation/util/addSamplesTracking';
import {addToWorklistActionFunction} from 'notebook/structured-tables/action-functions/addToWorklistActionFunction';
import {inventorySubmitActionFunction} from 'notebook/structured-tables/action-functions/inventorySubmitActionFunction';
import {ColumnGroup} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  isContainersStructuredTableColumn,
  isExistingBioentityStructuredTableColumn,
  isMeasurementAmountStructuredTableColumn,
  isParentStorableStructuredTableColumn,
  isPlateStructuredTableColumn,
  isPositionStructuredTableColumn,
  isTagValueStructuredTableColumn,
  isUnitReferenceStructuredTableColumn,
  isUnitSelectorStructuredTableColumn,
} from 'notebook/structured-tables/columns/TypePredicates';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import {InventoryTableRowUpdater} from 'notebook/structured-tables/row-updaters/InventoryTableRowUpdater';
import importRowsFromSpreadsheet from 'notebook/structured-tables/spreadsheet-import/importRowsFromSpreadsheet';
import {getColumnAdapter} from 'notebook/structured-tables/StructuredTableColumnAdapters';
import {
  ActionFunctions,
  BaseStructuredTable,
  CellErrorPosition,
  PrimarySample,
  PrimarySampleLookupType,
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableAddFromSourceType,
  StructuredTableAddOperation,
  StructuredTableColumn,
  StructuredTableColumnGroups,
  StructuredTableDomainGraphType,
  StructuredTableMetadata,
  StructuredTableRow,
  StructuredTableRowUpdater,
  StructuredTableSchema,
  TableValidationFn,
} from 'notebook/structured-tables/StructuredTableConstants';
import {StructuredTableRowUpdaterModel} from 'notebook/structured-tables/StructuredTableConstants';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {
  CellIndex,
  CustomEval,
  TableColumnType,
  USER_CREATED_COLUMN_TYPES,
} from 'notebook/tables/TableConstants';
import {
  areTypedValuesEquivalent,
  formatTypedValue,
  getStorableInfoFromTypedValue,
  isTypedValueEmpty,
  toStringTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {TransferTableColumnGroup} from 'notebook/transfer-tables/TransferTableConstants';
import {PlateSchema, PlateSchemaType, StorableSchema} from 'samples/storables/StorableSchemaConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import APIUtil from 'util/APIUtil';
import {assertNever} from 'util/Types';
import {WORKLIST_FILL_LOCATION} from 'worklists/WorklistConstants';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import submitInventoryRowsActionFunction from './submitInventoryRowsActionFunction';
export interface InventoryTableRowMetadata {
  unlockRowAfterSubmit?: boolean;
}
// Keep in sync with benchling/notebook/structured_tables/constants/inventory_tables.py
export const CONTAINER_COLUMN_NAME = 'Container';
export const PLATE_COLUMN_NAME = 'Plate';
export const POSITION_COLUMN_NAME = 'Position';
export const CONTENTS_COLUMN_NAME = 'Contents';
export const LOCATION_COLUMN_NAME = 'Location';
export const QUANTITY_COLUMN_NAME = 'Quantity';
export const QUANTITY_UNITS_COLUMN_NAME = 'Quantity Units';
export const ADD_DEDUCT_COLUMN_NAME = 'Add/deduct by';
export const CONCENTRATION_COLUMN_NAME = 'Concentration';
export const CONCENTRATION_UNITS_COLUMN_NAME = 'Concentration Units';

interface ColumnCustomEvalInfo {
  customEvalFn: CustomEval<{
    tableMetadata: StructuredTableMetadata & InventoryTableMetadata;
    rowMetadata: InventoryTableRowMetadata;
  }>;
  getColumnIndexesOfDependencies(columns: Array<StructuredTableColumn>): Array<number>;
}

// An error message to show if the container or plate well is listed twice
export const CONTAINER_ALREADY_ENTERED_ERROR = 'Cannot update same container in multiple rows';
export const PLATE_WELL_ALREADY_ENTERED_ERROR = 'Cannot update same plate well in multiple rows';

// Error messages for incomplete concentration inputs
export const CONCENTRATION_AMOUNT_REQUIRED_ERROR =
  'Please enter a concentration amount or select `None` as a concentration unit.';
export const CONCENTRATION_UNITS_REQUIRED_ERROR = 'Please select a concentration unit.';

// Error messages for missing columns
export const MISSING_CONCENTRATION_COL_ERROR_TYPE = 'Value error';
export const MISSING_CONCENTRATION_AMOUNT_COL_ERROR = 'Please add a concentration amount column.';
export const MISSING_CONCENTRATION_UNITS_COL_ERROR = 'Please add a concentration units column.';

export const DELETABLE_COLUMN_GROUPS = [
  StructuredTableColumnGroups.CONCENTRATION,
  TransferTableColumnGroup.TARGET,
];

export default class InventoryStructuredTableAdapter extends GenericStructuredTableAdapter<
  InventoryTableMetadata,
  InventoryTableRowMetadata
> {
  getDisplayName(
    structuredTable: StructuredTable<InventoryTableMetadata, InventoryTableRowMetadata>
  ): string {
    switch (structuredTable.metadata.storableSchemaType) {
      case InventoryStructuredTableSchemaType.CONTAINER:
        return 'Update containers';
      case InventoryStructuredTableSchemaType.PLATE:
        return 'Update wells or plates';
      default:
        assert(false, `Unexpected schema type ${structuredTable.metadata.storableSchemaType}`);
    }
  }

  getBlankRowMetadata(tableMetadata: InventoryTableMetadata): InventoryTableRowMetadata {
    // unlockRowAfterSubmit is lifted onto the row metadata for easy access when only the row is accessible
    // This value is not modifiable after the table is inserted
    return {unlockRowAfterSubmit: tableMetadata.unlockRowsAfterSubmit};
  }

  // Keep in sync with benchling/notebook/structured_tables/instantiators/inventory_table_instantiator.py
  getStorableColumns(metadata: InventoryTableMetadata): Array<StructuredTableColumn> {
    const quantityColumns = [
      {
        ...makeStructuredTableColumn({
          columnType: TableColumnType.MEASUREMENT_AMOUNT,
          name: QUANTITY_COLUMN_NAME,
          columnGroup: StructuredTableColumnGroups.QUANTITY,
        }),
      },
      {
        ...makeStructuredTableColumn({
          columnType: TableColumnType.UNIT_REFERENCE,
          name: QUANTITY_UNITS_COLUMN_NAME,
          columnGroup: StructuredTableColumnGroups.QUANTITY,
        }),
      },
      {
        ...makeStructuredTableColumn({
          columnType: TableColumnType.MEASUREMENT_AMOUNT,
          name: ADD_DEDUCT_COLUMN_NAME,
          columnGroup: TransferTableColumnGroup.TARGET,
        }),
      },
    ];
    const concentrationColumns = [
      {
        ...makeStructuredTableColumn({
          columnType: TableColumnType.MEASUREMENT_AMOUNT,
          name: CONCENTRATION_COLUMN_NAME,
          columnGroup: StructuredTableColumnGroups.CONCENTRATION,
          isReadOnly: false,
        }),
      },
      {
        ...makeStructuredTableColumn({
          columnType: TableColumnType.UNIT_REFERENCE,
          name: CONCENTRATION_UNITS_COLUMN_NAME,
          columnGroup: StructuredTableColumnGroups.CONCENTRATION,
          isReadOnly: false,
        }),
      },
    ];
    switch (metadata.storableSchemaType) {
      case InventoryStructuredTableSchemaType.CONTAINER:
        return [
          {
            ...makeStructuredTableColumn({
              columnType: TableColumnType.CONTAINERS,
              name: CONTAINER_COLUMN_NAME,
              columnGroup: null,
            }),
            isRequired: true,
          },
          {
            ...makeStructuredTableColumn({
              columnType: TableColumnType.EXISTING_BIOENTITY,
              name: CONTENTS_COLUMN_NAME,
              columnGroup: null,
            }),
            isReadOnly: true,
          },
          {
            ...makeStructuredTableColumn({
              columnType: TableColumnType.PARENT_STORABLE,
              name: LOCATION_COLUMN_NAME,
              columnGroup: null,
            }),
            isReadOnly: true,
          },
          {
            ...makeStructuredTableColumn({
              columnType: TableColumnType.POSITION,
              name: POSITION_COLUMN_NAME,
              columnGroup: null,
            }),
          },
          ...quantityColumns,
          ...concentrationColumns,
        ];
      case InventoryStructuredTableSchemaType.PLATE:
        return [
          {
            ...makeStructuredTableColumn({
              columnType: TableColumnType.PLATE,
              name: PLATE_COLUMN_NAME,
              columnGroup: null,
            }),
            isRequired: true,
          },
          {
            ...makeStructuredTableColumn({
              columnType: TableColumnType.POSITION,
              name: POSITION_COLUMN_NAME,
              columnGroup: null,
            }),
            isRequired: true,
          },
          {
            ...makeStructuredTableColumn({
              columnType: TableColumnType.EXISTING_BIOENTITY,
              name: CONTENTS_COLUMN_NAME,
              columnGroup: null,
            }),
            isReadOnly: true,
          },
          {
            ...makeStructuredTableColumn({
              columnType: TableColumnType.PARENT_STORABLE,
              name: LOCATION_COLUMN_NAME,
              columnGroup: null,
            }),
            isReadOnly: true,
          },
          ...quantityColumns,
          ...concentrationColumns,
        ];
      default:
        assert(false, `Unexpected source type ${metadata.storableSchemaType}`);
    }
  }

  private extractStorableSchemaAndColumnGroup({
    storableSchemaType,
    schemas,
  }: {
    storableSchemaType: InventoryStructuredTableSchemaType;
    schemas: Array<SchemaAndColumnGroup>;
  }): SchemaAndColumnGroup | null {
    switch (storableSchemaType) {
      case InventoryStructuredTableSchemaType.CONTAINER: {
        return extractContainerSchemaAndColumnGroup(schemas);
      }
      case InventoryStructuredTableSchemaType.PLATE: {
        return extractPlateSchemaAndColumnGroup(schemas);
      }
      default:
        assert(false, `Unexpected source type ${storableSchemaType}`);
        return null;
    }
  }

  override injectFrontEndMetadata({
    column,
    schemas,
    tableMetadata,
  }: {
    column: StructuredTableColumn;
    schemas: Array<SchemaAndColumnGroup>;
    tableMetadata: InventoryTableMetadata;
  }): StructuredTableColumn {
    const storableSchema = this.extractStorableSchemaAndColumnGroup({
      storableSchemaType: tableMetadata.storableSchemaType,
      schemas,
    });
    if (isContainersStructuredTableColumn(column)) {
      return storableSchema
        ? {
            ...column,
            frontEndMetadata: {
              containerSchemaId: storableSchema.id,
              containerSchemaName: storableSchema.name,
            },
          }
        : column;
    } else if (
      isExistingBioentityStructuredTableColumn(column) ||
      isParentStorableStructuredTableColumn(column) ||
      isPositionStructuredTableColumn(column) ||
      isTagValueStructuredTableColumn(column)
    ) {
      return storableSchema
        ? {
            ...column,
            frontEndMetadata: {
              storableSchemaId: storableSchema.id,
            },
          }
        : column;
    } else if (isMeasurementAmountStructuredTableColumn(column)) {
      return {
        ...column,
        frontEndMetadata: {
          storableSchemaId: storableSchema?.id,
          allowQS: false,
          allowZero: column.columnGroup !== TransferTableColumnGroup.TARGET,
        },
      };
    } else if (isUnitSelectorStructuredTableColumn(column)) {
      return {
        ...column,
        frontEndMetadata: {
          storableSchemaId: storableSchema?.id,
          acceptedUnits:
            column.columnGroup === StructuredTableColumnGroups.CONCENTRATION
              ? VALID_CONCENTRATION_UNITS
              : BenchlingConfig.INVENTORY_ENABLE_SOLID_UNITS_FOR_CONTAINER
                ? VALID_CONTAINER_QUANITY_UNITS
                : VALID_VOLUME_UNITS,
        },
      };
    } else if (isUnitReferenceStructuredTableColumn(column)) {
      return {
        ...column,
        frontEndMetadata: {
          storableSchemaId: storableSchema?.id,
          unitTypesFilterPredicate:
            column.columnGroup === StructuredTableColumnGroups.CONCENTRATION
              ? concentrationUnitTypePredicate
              : containerQuantityUnitTypePredicate,
        },
      };
    }
    return column;
  }

  private getColumnIndexesOfContainerDependencies = (
    columns: Array<StructuredTableColumn>
  ): Array<number> => [columns.findIndex(({columnType}) => columnType === TableColumnType.CONTAINERS)];

  private getColumnIndexesOfContainerConcentrationDependencies = (
    columns: Array<StructuredTableColumn>
  ): Array<number> => [
    columns.findIndex(({columnType}) => columnType === TableColumnType.CONTAINERS),
    columns.findIndex(({columnType}) => columnType === TableColumnType.EXISTING_BIOENTITY),
  ];

  private getColumnIndexesOfPlateDependencies = (columns: Array<StructuredTableColumn>): Array<number> => [
    columns.findIndex(({columnType}) => columnType === TableColumnType.PLATE),
  ];

  private getColumnIndexesOfPlatePositionDependencies = (
    columns: Array<StructuredTableColumn>
  ): Array<number> => [
    columns.findIndex(({columnType}) => columnType === TableColumnType.PLATE),
    columns.findIndex(({columnType}) => columnType === TableColumnType.POSITION),
  ];

  private getColumnIndexesOfPlateWellConcentrationDependencies = (
    columns: Array<StructuredTableColumn>
  ): Array<number> => [
    columns.findIndex(({columnType}) => columnType === TableColumnType.PLATE),
    columns.findIndex(({columnType}) => columnType === TableColumnType.POSITION),
    columns.findIndex(({columnType}) => columnType === TableColumnType.EXISTING_BIOENTITY),
  ];

  private getColumnCustomEvalInfo(
    tableMetadata: InventoryTableMetadata,
    column: StructuredTableColumn
  ): ColumnCustomEvalInfo | null {
    switch (tableMetadata.storableSchemaType) {
      case InventoryStructuredTableSchemaType.CONTAINER:
        if (isContainersStructuredTableColumn(column)) {
          return {
            customEvalFn: ContainerSourceTypeCustomEval.sourceContainerCustomEval,
            getColumnIndexesOfDependencies: () => [],
          };
        }
        if (isExistingBioentityStructuredTableColumn(column)) {
          return {
            customEvalFn: ContainerSourceTypeCustomEval.getSourceEntityCustomEval({
              lockIfContainerValIsEmpty: true,
              containerSchemaId: column.frontEndMetadata?.storableSchemaId,
              allowMultiContentsContainer: true,
            }),
            getColumnIndexesOfDependencies: this.getColumnIndexesOfContainerDependencies,
          };
        }
        if (isParentStorableStructuredTableColumn(column)) {
          return {
            customEvalFn: ParentStorableCustomEval.getContainerParentStorableCustomEval(
              true,
              column.frontEndMetadata?.storableSchemaId
            ),
            getColumnIndexesOfDependencies: this.getColumnIndexesOfContainerDependencies,
          };
        }
        if (isPositionStructuredTableColumn(column)) {
          return {
            customEvalFn: ParentStorableCustomEval.getContainerGridPositionCustomEval(
              true,
              column.frontEndMetadata?.storableSchemaId
            ),
            getColumnIndexesOfDependencies: this.getColumnIndexesOfContainerDependencies,
          };
        }
        if (
          isMeasurementAmountStructuredTableColumn(column) &&
          column.columnGroup === TransferTableColumnGroup.TARGET
        ) {
          return {
            customEvalFn: InventoryQuantityCustomEval.addDeductFromSourceContainerCustomEval,
            getColumnIndexesOfDependencies: this.getColumnIndexesOfContainerDependencies,
          };
        }
        if (isTagValueStructuredTableColumn(column)) {
          return {
            customEvalFn: SchemaTagsCustomEval.containerSchemaTagCustomEval,
            getColumnIndexesOfDependencies: this.getColumnIndexesOfContainerDependencies,
          };
        }
        if (
          isMeasurementAmountStructuredTableColumn(column) &&
          column.columnGroup !== TransferTableColumnGroup.TARGET &&
          column.columnGroup !== StructuredTableColumnGroups.CONCENTRATION
        ) {
          return {
            customEvalFn: InventoryQuantityCustomEval.getQuantityAmountFromSourceContainerCustomEval({
              containerSchemaId: column.frontEndMetadata?.storableSchemaId,
            }),
            getColumnIndexesOfDependencies: this.getColumnIndexesOfContainerDependencies,
          };
        }
        if (
          (isUnitSelectorStructuredTableColumn(column) || isUnitReferenceStructuredTableColumn(column)) &&
          column.columnGroup !== StructuredTableColumnGroups.CONCENTRATION
        ) {
          return {
            customEvalFn: InventoryQuantityCustomEval.getQuantityUnitsFromSourceContainerCustomEval({
              containerSchemaId: column.frontEndMetadata?.storableSchemaId,
            }),
            getColumnIndexesOfDependencies: this.getColumnIndexesOfContainerDependencies,
          };
        }
        if (
          isMeasurementAmountStructuredTableColumn(column) &&
          column.columnGroup === StructuredTableColumnGroups.CONCENTRATION
        ) {
          return {
            customEvalFn: InventoryConcentrationCustomEval.getConcentrationAmountFromContainerCustomEval({
              containerSchemaId: column.frontEndMetadata?.storableSchemaId,
            }),
            getColumnIndexesOfDependencies: this.getColumnIndexesOfContainerConcentrationDependencies,
          };
        }
        if (
          (isUnitSelectorStructuredTableColumn(column) || isUnitReferenceStructuredTableColumn(column)) &&
          column.columnGroup === StructuredTableColumnGroups.CONCENTRATION
        ) {
          return {
            customEvalFn: InventoryConcentrationCustomEval.getConcentrationUnitsFromContainerCustomEval({
              containerSchemaId: column.frontEndMetadata?.storableSchemaId,
            }),
            getColumnIndexesOfDependencies: this.getColumnIndexesOfContainerConcentrationDependencies,
          };
        }
        return null;
      case InventoryStructuredTableSchemaType.PLATE:
        if (isPlateStructuredTableColumn(column)) {
          return {
            customEvalFn: PlateSourceTypeCustomEval.sourcePlateCustomEval,
            getColumnIndexesOfDependencies: () => [],
          };
        }
        if (isPositionStructuredTableColumn(column)) {
          return {
            customEvalFn: PlateSourceTypeCustomEval.getSourcePositionCustomEval({
              allowEmptyWell: true,
              allowMultiContentsWell: true,
            }),
            getColumnIndexesOfDependencies: this.getColumnIndexesOfPlateDependencies,
          };
        }
        if (isExistingBioentityStructuredTableColumn(column)) {
          return {
            customEvalFn: PlateSourceTypeCustomEval.getSourceEntityCustomEval({
              plateSchemaId: column.frontEndMetadata?.storableSchemaId,
              plateSchemaType: PlateSchemaType.FIXED_PLATE,
              allowMultiContentsWell: true,
            }),
            getColumnIndexesOfDependencies: this.getColumnIndexesOfPlatePositionDependencies,
          };
        }
        if (isParentStorableStructuredTableColumn(column)) {
          return {
            customEvalFn: ParentStorableCustomEval.getPlateParentStorableCustomEval(
              true,
              column.frontEndMetadata?.storableSchemaId
            ),
            getColumnIndexesOfDependencies: this.getColumnIndexesOfPlateDependencies,
          };
        }
        if (
          isMeasurementAmountStructuredTableColumn(column) &&
          column.columnGroup === TransferTableColumnGroup.TARGET
        ) {
          return {
            customEvalFn: InventoryQuantityCustomEval.addDeductFromSourcePlateCustomEval,
            getColumnIndexesOfDependencies: this.getColumnIndexesOfPlatePositionDependencies,
          };
        }
        if (isTagValueStructuredTableColumn(column)) {
          return {
            customEvalFn: SchemaTagsCustomEval.getPlateWellSchemaTagCustomEval({
              plateSchemaId: column.frontEndMetadata?.storableSchemaId,
            }),
            getColumnIndexesOfDependencies: this.getColumnIndexesOfPlatePositionDependencies,
          };
        }
        if (
          isMeasurementAmountStructuredTableColumn(column) &&
          column.columnGroup !== TransferTableColumnGroup.TARGET &&
          column.columnGroup !== StructuredTableColumnGroups.CONCENTRATION
        ) {
          return {
            customEvalFn: InventoryQuantityCustomEval.getQuantityAmountFromSourcePlateCustomEval({
              plateSchemaId: column.frontEndMetadata?.storableSchemaId,
            }),
            getColumnIndexesOfDependencies: this.getColumnIndexesOfPlatePositionDependencies,
          };
        }
        if (
          (isUnitSelectorStructuredTableColumn(column) || isUnitReferenceStructuredTableColumn(column)) &&
          column.columnGroup !== StructuredTableColumnGroups.CONCENTRATION
        ) {
          return {
            customEvalFn: InventoryQuantityCustomEval.getQuantityUnitsFromSourcePlateCustomEval({
              plateSchemaId: column.frontEndMetadata?.storableSchemaId,
            }),
            getColumnIndexesOfDependencies: this.getColumnIndexesOfPlatePositionDependencies,
          };
        }
        if (
          isMeasurementAmountStructuredTableColumn(column) &&
          column.columnGroup === StructuredTableColumnGroups.CONCENTRATION
        ) {
          return {
            customEvalFn: InventoryConcentrationCustomEval.getConcentrationAmountFromPlateCustomEval({
              plateSchemaId: column.frontEndMetadata?.storableSchemaId,
            }),
            getColumnIndexesOfDependencies: this.getColumnIndexesOfPlateWellConcentrationDependencies,
          };
        }
        if (
          (isUnitSelectorStructuredTableColumn(column) || isUnitReferenceStructuredTableColumn(column)) &&
          column.columnGroup === StructuredTableColumnGroups.CONCENTRATION
        ) {
          return {
            customEvalFn: InventoryConcentrationCustomEval.getConcentrationUnitsFromPlateCustomEval({
              plateSchemaId: column.frontEndMetadata?.storableSchemaId,
            }),
            getColumnIndexesOfDependencies: this.getColumnIndexesOfPlateWellConcentrationDependencies,
          };
        }
        return null;
      default:
        assert(false, `Unexpected schema type ${tableMetadata.storableSchemaType}`);
    }
  }

  override getTableSpecificCustomEvalFn(
    column: StructuredTableColumn,
    row: StructuredTableRow<InventoryTableRowMetadata>,
    tableMetadata: InventoryTableMetadata
  ): CustomEval<{
    tableMetadata: InventoryTableMetadata;
    rowMetadata: InventoryTableRowMetadata;
  }> | null {
    // Custom eval should be disabled for finalized rows when you cannot resubmit them
    // Otherwise, we keep custom eval even on finalized rows to leverage the validation custom eval and
    // preserve consistent behavior of the looked up columns
    if (this.isRowFinalizedWithoutResubmit(row, tableMetadata)) {
      return null;
    }

    return this.getColumnCustomEvalInfo(tableMetadata, column)?.customEvalFn ?? null;
  }

  override getTableSpecificCustomEvalArgs(
    columns: Array<StructuredTableColumn>,
    currentColumnIndex: number,
    currentRowIndex: number,
    tableMetadata: InventoryTableMetadata,
    toSpreadsheetIndex: (cellId: CellIndex) => string
  ): Array<string> {
    const dependsOnColumnIndexes =
      this.getColumnCustomEvalInfo(
        tableMetadata,
        columns[currentColumnIndex]
      )?.getColumnIndexesOfDependencies(columns) ?? [];

    return dependsOnColumnIndexes.map((dependencyIndex, slotIndex) => {
      assert(
        dependencyIndex !== -1,
        `Could not find dependency at slot ${slotIndex} for column at index ${currentColumnIndex}`
      );
      return `=${toSpreadsheetIndex({row: currentRowIndex, column: dependencyIndex})}`;
    });
  }

  private getPrimarySchemaId(structuredTable: InventoryStructuredTable): string | null {
    const schemaType = structuredTable.metadata.storableSchemaType;
    const schema = structuredTable.schemas.find((schema) =>
      schemaType === InventoryStructuredTableSchemaType.CONTAINER
        ? APIUtil.isContainerSchema(schema.id)
        : APIUtil.isPlateSchema(schema.id)
    );
    return schema?.id ?? null;
  }

  override getAddFromSourceOperations(
    entryId: string,
    structuredTable?: InventoryStructuredTable,
    addAIDataEntryRows?: (
      initialPromptText?: string,
      initialSelectedTableName?: string,
      result?: AddStructuredTableRowsUsingAIResult | null
    ) => void
  ): Array<StructuredTableAddOperation<InventoryTableMetadata, InventoryTableRowMetadata>> {
    const baseTrackingParams = {
      module: 'ELN',
      actionOrigin: ActionOrigin.NOTEBOOK,
      entryId,
      sampleType:
        structuredTable?.metadata?.storableSchemaType === InventoryStructuredTableSchemaType.PLATE
          ? 'plate_or_well'
          : 'container',
      tableType: 'inventory_table',
    };
    const ops: Array<StructuredTableAddOperation<InventoryTableMetadata, InventoryTableRowMetadata>> = [
      {
        type: StructuredTableAddFromSourceType.ADD_FROM_TABLE,
        onSelect: ({note, structuredTable, addSamples}) => {
          addSamplesFromStructuredTable({
            destinationTable: structuredTable,
            adapter: this,
            note,
            entryId,
            addSamples,
            trackingParams: {
              ...baseTrackingParams,
              structuredTableId: structuredTable.apiId ?? null,
              schemaId: this.getPrimarySchemaId(structuredTable),
            } as AddSamplesToTableTrackingParams,
          });
        },
      },
    ];

    if (BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS) {
      ops.push({
        type: StructuredTableAddFromSourceType.ADD_FROM_WORKLIST,
        onSelect: ({structuredTable, addSamples}) => {
          addSamplesFromWorklist(
            structuredTable,
            this,
            addSamples,
            WORKLIST_FILL_LOCATION.INVENTORY_TABLE,
            undefined,
            {
              ...baseTrackingParams,
              structuredTableId: structuredTable.apiId ?? null,
              schemaId: this.getPrimarySchemaId(structuredTable),
            } as AddSamplesToTableTrackingParams
          );
        },
      });
    }

    ops.push({
      type: StructuredTableAddFromSourceType.ADD_FROM_SPREADSHEET,
      onSelect: async ({structuredTable, addFullRows}) => {
        const schemaType = structuredTable.metadata.storableSchemaType;
        const schema = structuredTable.schemas.find((schema) =>
          schemaType === InventoryStructuredTableSchemaType.CONTAINER
            ? APIUtil.isContainerSchema(schema.id)
            : APIUtil.isPlateSchema(schema.id)
        );

        const rows = await importRowsFromSpreadsheet({
          structuredTable,
          schemas:
            schema != null
              ? schemaType === InventoryStructuredTableSchemaType.CONTAINER
                ? {containerSchema: schema as StorableSchema}
                : {plateSchema: schema as PlateSchema}
              : {},
          getSpreadsheetTypeForColumn: (column) => this.getSpreadsheetTypeForColumn(column, schemaType),
        });

        if (rows) {
          addFullRows(rows);
          trackAddSamples({
            ...baseTrackingParams,
            structuredTableId: structuredTable.apiId ?? null,
            schemaId: this.getPrimarySchemaId(structuredTable),
            numSamples: rows.length,
            sourceId: null,
            sourceType: 'spreadsheet',
          } as CompleteAddSamplesTrackingParams);
        }
      },
    });

    ops.push({
      type: StructuredTableAddFromSourceType.ADD_FROM_BOX_OR_PLATE,
      onSelect: ({structuredTable, addFullRows}) => {
        const destinationSchema = structuredTable.schemas.find((schema) =>
          structuredTable.metadata.storableSchemaType === InventoryStructuredTableSchemaType.CONTAINER
            ? APIUtil.isContainerSchema(schema.id)
            : APIUtil.isPlateSchema(schema.id)
        );
        addSamplesFromBoxOrPlate(
          structuredTable,
          this,
          addFullRows,
          {
            destinationSchemaId: destinationSchema?.id ?? null,
            destinationSchemaName: destinationSchema?.name ?? null,
            destinationColumnType:
              structuredTable.metadata.storableSchemaType === InventoryStructuredTableSchemaType.CONTAINER
                ? TableColumnType.CONTAINERS
                : TableColumnType.PLATE,
            registryId: structuredTable.metadata.fallbackRegistryId,
          },
          undefined,
          {
            ...baseTrackingParams,
            structuredTableId: structuredTable.apiId ?? null,
            schemaId: this.getPrimarySchemaId(structuredTable),
          } as AddSamplesToTableTrackingParams
        );
      },
    });

    ops.push({
      type: StructuredTableAddFromSourceType.ADD_FROM_BARCODE_SCANNER,
      onSelect: ({structuredTable, addSamples}) => {
        addSamplesFromBarcodeScanner(
          structuredTable,
          this,
          addSamples,
          {
            destinationSchemaId:
              structuredTable.schemas.find((schema) =>
                structuredTable.metadata.storableSchemaType === InventoryStructuredTableSchemaType.CONTAINER
                  ? APIUtil.isContainerSchema(schema.id)
                  : APIUtil.isPlateSchema(schema.id)
              )?.id ?? null,
            destinationColumnType:
              structuredTable.metadata.storableSchemaType === InventoryStructuredTableSchemaType.CONTAINER
                ? TableColumnType.CONTAINERS
                : TableColumnType.PLATE,
          },
          undefined,
          {
            ...baseTrackingParams,
            structuredTableId: structuredTable.apiId ?? null,
            schemaId: this.getPrimarySchemaId(structuredTable),
          } as AddSamplesToTableTrackingParams
        );
      },
    });

    if (BenchlingConfig.ELN_ENABLE_AI_DATA_ENTRY) {
      ops.push({
        type: StructuredTableAddFromSourceType.ADD_USING_AI,
        onSelect: ({structuredTable}) => {
          addAIDataEntryRows?.(undefined, structuredTable.name);
        },
      });
    }

    return ops;
  }

  private getSpreadsheetTypeForColumn(
    column: StructuredTableColumn,
    schemaType: InventoryStructuredTableSchemaType
  ): SpreadsheetBulkImportConstants.COLUMN_TYPE | null {
    switch (column.columnType) {
      case TableColumnType.CONTAINERS:
        return SpreadsheetBulkImportConstants.COLUMN_TYPE.INPUT_CONTAINER_BARCODE;
      case TableColumnType.PLATE:
        return SpreadsheetBulkImportConstants.COLUMN_TYPE.INPUT_GRID_BARCODE;
      case TableColumnType.POSITION:
        return schemaType === InventoryStructuredTableSchemaType.CONTAINER
          ? null
          : SpreadsheetBulkImportConstants.COLUMN_TYPE.INPUT_GRID_COORDINATES;
      case TableColumnType.MEASUREMENT_AMOUNT:
        return column.columnGroup === TransferTableColumnGroup.TARGET
          ? SpreadsheetBulkImportConstants.COLUMN_TYPE.ADD_DEDUCT_AMOUNT
          : null;
      case TableColumnType.TAG_VALUE:
        return SpreadsheetBulkImportConstants.COLUMN_TYPE.CONTAINER_SCHEMA_FIELD;
      case TableColumnType.EXISTING_BIOENTITY:
      case TableColumnType.PARENT_STORABLE:
      case TableColumnType.UNIT_SELECTOR:
      case TableColumnType.UNIT_REFERENCE:
      case TableColumnType.MISSING_FIELD:
      case TableColumnType.STUDIES:
      case TableColumnType.DEDICATED_LOOKUP:
      case TableColumnType.CUSTOM:
        return null;
      default:
        assert(false, `Unsupported column type ${column.columnType}`);
    }
  }

  private isRowFinalizedWithoutResubmit(
    row: StructuredTableRow<InventoryTableRowMetadata>,
    tableMetadata: InventoryTableMetadata
  ): boolean {
    return Boolean(row.isFinalized && !tableMetadata.unlockRowsAfterSubmit);
  }

  override isPositionReadOnly(
    currentColumnIndex: number,
    row: StructuredTableRow<InventoryTableRowMetadata>,
    structuredTable: InventoryStructuredTable
  ): boolean {
    const {columns, metadata} = structuredTable;
    const {columnType, columnGroup} = columns[currentColumnIndex];
    switch (columnType) {
      case TableColumnType.EXISTING_BIOENTITY:
      case TableColumnType.PARENT_STORABLE:
        return true;
      case TableColumnType.UNIT_SELECTOR:
      case TableColumnType.UNIT_REFERENCE:
        return (
          columnGroup !== StructuredTableColumnGroups.CONCENTRATION ||
          this.isRowFinalizedWithoutResubmit(row, metadata)
        );
      case TableColumnType.MEASUREMENT_AMOUNT:
        return (
          (columnGroup !== StructuredTableColumnGroups.CONCENTRATION &&
            columnGroup !== TransferTableColumnGroup.TARGET) ||
          this.isRowFinalizedWithoutResubmit(row, metadata)
        );
      case TableColumnType.TAG_VALUE:
        return this.isRowFinalizedWithoutResubmit(row, metadata);
      case TableColumnType.POSITION:
        // Unlike the other columns that are editable in finalized rows when resubmitting, position is not
        // as that is part of the identifying information for the row
        return (
          metadata.storableSchemaType === InventoryStructuredTableSchemaType.CONTAINER || row.isFinalized
        );
      case TableColumnType.STUDIES:
        return this.isRowFinalizedWithoutResubmit(row, metadata);
      default:
        return super.isPositionReadOnly(currentColumnIndex, row, structuredTable);
    }
  }

  override isColumnUserConfigurable(
    structuredTable: InventoryStructuredTable,
    column: StructuredTableColumn | null
  ): boolean {
    const editableTypes = [TableColumnType.TAG_VALUE, ...USER_CREATED_COLUMN_TYPES];
    const type = column?.columnType;
    if (type == null) {
      // If we don't know what kind of column this is, we'll just be safe and say it's not editable.
      // This _shouldn't_ occur for StructuredTables but there might be some weird historical detail that
      // we're missing.
      return false;
    }
    const isEditableType =
      editableTypes.includes(type) || (getColumnAdapter(type)?.isUserConfigurable ?? false);

    if (structuredTable.rows.some((row) => row.isFinalized)) {
      return false;
    }
    const columnGroup = column?.columnGroup;
    if (columnGroup != null) {
      return isEditableType || DELETABLE_COLUMN_GROUPS.includes(columnGroup);
    }
    return isEditableType;
  }

  override getActionFunctions(
    tableMetadata: InventoryTableMetadata,
    schemas: Array<StructuredTableSchema>
  ): ActionFunctions<InventoryTableMetadata, {}> {
    const inherited = super.getActionFunctions(tableMetadata, schemas);
    return {
      primary: [inventorySubmitActionFunction],
      secondary: [
        ...inherited.secondary!,
        notebookTableInventoryShortcutsActionFunction,
        addToWorklistActionFunction,
      ],
      overflow: [...inherited.overflow!],
      contextMenu: [submitInventoryRowsActionFunction],
      footer: inherited.footer ?? [],
    };
  }

  private getErrorsByCellPosition(
    existingPositionsById: {
      [storableId: string]: Array<{columnIndex: number; rowIndex: number; isFinalized: boolean}>;
    },
    storableSchemaType: InventoryStructuredTableSchemaType,
    unlockRowsAfterSubmit?: boolean
  ): Array<CellErrorPosition> {
    const errors: Array<CellErrorPosition> = [];
    for (const storableId of Object.keys(existingPositionsById)) {
      const positionsFound = existingPositionsById[storableId];
      // If at most one of the positions is unfinalized and rows are locked, then we ended up with multiple
      // rows with the same container or plate well because of resubmissions.  This is okay and not considered
      // an error. If rows are not frozen on submit, then containers/wells can be present only once
      const deduplicatedPositions = unlockRowsAfterSubmit
        ? positionsFound
        : positionsFound.filter(({isFinalized}) => !isFinalized);
      if (deduplicatedPositions.length > 1) {
        deduplicatedPositions.forEach(({columnIndex, rowIndex}) => {
          errors.push({
            columnIndex,
            rowIndex,
            error: {
              type: 'Error',
              message:
                storableSchemaType === InventoryStructuredTableSchemaType.CONTAINER
                  ? CONTAINER_ALREADY_ENTERED_ERROR
                  : PLATE_WELL_ALREADY_ENTERED_ERROR,
            },
          });
        });
      }
    }
    return errors;
  }

  private getErrorsForDuplicatesInNonFinalizedRows = (
    structuredTable: StructuredTable<InventoryTableMetadata, InventoryTableRowMetadata>,
    computedTable: Array<Array<EvaluatedCell>>
  ): Array<CellErrorPosition> => {
    const {
      metadata: {storableSchemaType, unlockRowsAfterSubmit},
    } = structuredTable;
    const {containerColumnIndex, plateColumnIndex, positionColumnIndex} =
      this.findPrimarySampleColumnIndices(structuredTable);

    switch (storableSchemaType) {
      case InventoryStructuredTableSchemaType.CONTAINER: {
        let existingContainerPositionsById: {
          [containerId: string]: Array<{columnIndex: number; rowIndex: number; isFinalized: boolean}>;
        } = {};
        structuredTable.rows.forEach((row, rowIndex) => {
          const {isFinalized} = row;
          const typedValue = computedTable[rowIndex][containerColumnIndex].value;
          const storableInfo = getStorableInfoFromTypedValue(typedValue);
          if (storableInfo != null) {
            existingContainerPositionsById = {
              ...existingContainerPositionsById,
              [storableInfo.id]: [
                ...(existingContainerPositionsById[storableInfo.id] != null
                  ? existingContainerPositionsById[storableInfo.id]
                  : []),
                {columnIndex: containerColumnIndex, rowIndex, isFinalized},
              ],
            };
          }
        });
        return this.getErrorsByCellPosition(
          existingContainerPositionsById,
          storableSchemaType,
          unlockRowsAfterSubmit
        );
      }
      case InventoryStructuredTableSchemaType.PLATE: {
        let existingPlateWellPositionsById: {
          [plateIdAndWell: string]: Array<{columnIndex: number; rowIndex: number; isFinalized: boolean}>;
        } = {};
        structuredTable.rows.forEach((row, rowIndex) => {
          const {isFinalized} = row;
          const plateTypedValue = computedTable[rowIndex][plateColumnIndex].value;
          const storableInfo = getStorableInfoFromTypedValue(plateTypedValue);
          const positionTypedValue = computedTable[rowIndex][positionColumnIndex].value;

          if (storableInfo != null && !areTypedValuesEquivalent(positionTypedValue, null)) {
            const plateIdAndWell = `${storableInfo.id}:${formatTypedValue(positionTypedValue)}`;
            existingPlateWellPositionsById = {
              ...existingPlateWellPositionsById,
              [plateIdAndWell]: [
                ...(existingPlateWellPositionsById[plateIdAndWell] != null
                  ? existingPlateWellPositionsById[plateIdAndWell]
                  : []),
                {columnIndex: positionColumnIndex, rowIndex, isFinalized},
              ],
            };
          }
        });
        return this.getErrorsByCellPosition(
          existingPlateWellPositionsById,
          storableSchemaType,
          unlockRowsAfterSubmit
        );
      }
      default:
        assert(false, `Unexpected source type ${storableSchemaType}`);
    }
  };

  private getErrorsForIncompleteConcentrationInputInNonFinalizedRows = (
    structuredTable: StructuredTable<InventoryTableMetadata, InventoryTableRowMetadata>,
    computedTable: Array<Array<EvaluatedCell>>
  ): Array<CellErrorPosition> => {
    const {concentrationAmountColumnIndex, concentrationUnitsColumnIndex} =
      this.findConcentrationColumnIndices(structuredTable);
    if (concentrationAmountColumnIndex == null && concentrationUnitsColumnIndex == null) {
      return []; // early return if either of the concentration columns are not present
    }
    const noneTypeValueForUnits = toStringTypedValue('None');
    const errors: Array<CellErrorPosition> = [];
    structuredTable.rows.forEach((row, rowIndex) => {
      const {isFinalized} = row;
      if (concentrationAmountColumnIndex == null) {
        errors.push({
          columnIndex: concentrationUnitsColumnIndex!,
          rowIndex,
          error: {
            type: MISSING_CONCENTRATION_COL_ERROR_TYPE,
            message: MISSING_CONCENTRATION_AMOUNT_COL_ERROR,
          },
        });
      } else if (concentrationUnitsColumnIndex == null) {
        errors.push({
          columnIndex: concentrationAmountColumnIndex,
          rowIndex,
          error: {
            type: MISSING_CONCENTRATION_COL_ERROR_TYPE,
            message: MISSING_CONCENTRATION_UNITS_COL_ERROR,
          },
        });
      } else {
        const amountTypedValue = computedTable[rowIndex][concentrationAmountColumnIndex].value;
        const unitsTypedValue = computedTable[rowIndex][concentrationUnitsColumnIndex].value;
        // Validate concentration on unfinalized rows & on finalized rows when they are unlocked
        if (!isFinalized || structuredTable.metadata.unlockRowsAfterSubmit) {
          // If the concentration amount is set to a value, the units can't be null or 'None'
          if (
            !isTypedValueEmpty(amountTypedValue) &&
            (isTypedValueEmpty(unitsTypedValue) ||
              areTypedValuesEquivalent(unitsTypedValue, noneTypeValueForUnits))
          ) {
            errors.push({
              columnIndex: concentrationUnitsColumnIndex,
              rowIndex,
              error: {
                type: 'Error',
                message: CONCENTRATION_UNITS_REQUIRED_ERROR,
              },
            });
          }

          // If the concentration units are set to a non-null value that is not 'None', the amount can't be null
          if (
            !isTypedValueEmpty(unitsTypedValue) &&
            !areTypedValuesEquivalent(unitsTypedValue, noneTypeValueForUnits) &&
            isTypedValueEmpty(amountTypedValue)
          ) {
            errors.push({
              columnIndex: concentrationAmountColumnIndex,
              rowIndex,
              error: {
                type: 'Error',
                message: CONCENTRATION_AMOUNT_REQUIRED_ERROR,
              },
            });
          }
        }
      }
    });
    return errors;
  };

  /**
   * Return a validation function that checks for duplicate containers or plate wells in unfinalized rows.
   */
  override getTableValidationFns(): Array<
    TableValidationFn<InventoryTableMetadata, InventoryTableRowMetadata>
  > {
    const inventoryTableValidationFns = [
      this.getErrorsForDuplicatesInNonFinalizedRows,
      this.getErrorsForIncompleteConcentrationInputInNonFinalizedRows,
    ];
    return [...super.getTableValidationFns(), ...inventoryTableValidationFns];
  }

  // Keep columns sync with inventory_table_harness and the backend inventory_table_instantiator
  override _getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string | null;
    metadata: InventoryTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[p: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    const storableColumns = this.getStorableColumns(metadata);
    const containerSchemaAndColumnGroup = schemas && extractContainerSchemaAndColumnGroup(schemas);
    const schemaTagColumns = super
      ._getAllSupportedColumns({
        entryId,
        metadata,
        schemas: containerSchemaAndColumnGroup ? [containerSchemaAndColumnGroup] : [],
        selectorsById,
        structuredTableInsertionContext,
      })
      .map((column) => ({
        ...column,
        isReadOnly: false,
      }));
    return [...storableColumns, ...schemaTagColumns];
  }

  /**
   * Returns column indices for concentration columns present in an inventory table
   */
  findConcentrationColumnIndices(
    structuredTable: StructuredTable<InventoryTableMetadata, InventoryTableRowMetadata>
  ): {
    concentrationAmountColumnIndex: number | null;
    concentrationUnitsColumnIndex: number | null;
  } {
    let concentrationAmountColumnIndex = null;
    let concentrationUnitsColumnIndex = null;

    for (const [colIdx, column] of structuredTable.columns.entries()) {
      if (column.columnGroup === StructuredTableColumnGroups.CONCENTRATION) {
        if (column.columnType === TableColumnType.MEASUREMENT_AMOUNT) {
          concentrationAmountColumnIndex = colIdx;
        } else {
          concentrationUnitsColumnIndex = colIdx;
        }
      }
    }

    return {
      concentrationAmountColumnIndex,
      concentrationUnitsColumnIndex,
    };
  }

  /**
   * Returns column indices for primary samples present in an inventory table (container or plate + position).
   *
   * Note that the column index will be -1 for samples that aren't present in the given table.
   */
  private findPrimarySampleColumnIndices(
    structuredTable: StructuredTable<InventoryTableMetadata, InventoryTableRowMetadata>
  ): {
    containerColumnIndex: number;
    plateColumnIndex: number;
    positionColumnIndex: number;
  } {
    let containerColumnIndex = -1;
    let plateColumnIndex = -1;
    let positionColumnIndex = -1;

    for (const [colIdx, column] of structuredTable.columns.entries()) {
      if (column.columnType === TableColumnType.CONTAINERS) {
        containerColumnIndex = colIdx;
      } else if (column.columnType === TableColumnType.PLATE) {
        plateColumnIndex = colIdx;
      } else if (column.columnType === TableColumnType.POSITION) {
        positionColumnIndex = colIdx;
      }
    }

    return {
      containerColumnIndex,
      plateColumnIndex,
      positionColumnIndex,
    };
  }

  private getPrimarySamplesForRow({
    structuredTable,
    computedTable,
    rowIndex,
  }: {
    structuredTable: StructuredTable<InventoryTableMetadata, InventoryTableRowMetadata>;
    computedTable?: Array<Array<EvaluatedCell>>;
    rowIndex: number;
  }): Array<PrimarySample> {
    const {containerColumnIndex, plateColumnIndex, positionColumnIndex} =
      this.findPrimarySampleColumnIndices(structuredTable);
    const primarySamples: Array<PrimarySample> = [];
    const {
      metadata: {storableSchemaType},
    } = structuredTable;
    const cells = structuredTable.rows[rowIndex].cells;

    switch (storableSchemaType) {
      case InventoryStructuredTableSchemaType.CONTAINER: {
        const computedContainer = computedTable?.[rowIndex][containerColumnIndex];
        const typedValue = computedContainer?.value ?? cells[containerColumnIndex].typedValue;
        const storableInfo = typedValue != null ? getStorableInfoFromTypedValue(typedValue) : null;

        if (
          storableInfo != null &&
          // `resolveStorableTypedValue` will return an error if the storable is not a container / has the
          // wrong schema. In such cases, we want to prevent downstream InventoryTableRowUpdater from
          // updating the row, as it has historically crashed (BNCH-41571)...
          computedContainer?.error == null &&
          // ... however, this is best-effort, as errors are suppressed for config tables in Entry Templates
          // (see Evaluator.ts:shouldSuppressValidation). At the very least, we confirm the API ID is for
          // a container as that definitively crashes InventoryTableRowUpdater (CUST-9368).
          APIUtil.isContainer(storableInfo.id)
        ) {
          primarySamples.push({
            schemaId: null,
            columnGroup: null,
            sampleLookupInfo: {
              sampleType: PrimarySampleLookupType.DIRECT,
              idForLookup: storableInfo.id,
            },
          });
        }
        break;
      }
      case InventoryStructuredTableSchemaType.PLATE: {
        const computedPlate = computedTable?.[rowIndex][plateColumnIndex];
        const plateTypedValue = computedPlate?.value ?? cells[plateColumnIndex].typedValue;
        const storableInfo = plateTypedValue != null ? getStorableInfoFromTypedValue(plateTypedValue) : null;

        const computedPosition = computedTable?.[rowIndex][positionColumnIndex];
        const positionTypedValue = computedPosition?.value ?? cells[positionColumnIndex].typedValue ?? null;

        if (
          storableInfo != null &&
          computedPlate?.error == null &&
          !areTypedValuesEquivalent(positionTypedValue, null) &&
          computedPosition?.error == null
        ) {
          primarySamples.push({
            schemaId: null,
            columnGroup: null,
            sampleLookupInfo: {
              sampleType: PrimarySampleLookupType.PLATE_POSITION_DUPLE,
              idForLookup: storableInfo.id,
              plateWellPosition: formatTypedValue(positionTypedValue!),
            },
          });
        }
        break;
      }
      default:
        assert(false, `Unexpected source type ${storableSchemaType}`);
    }
    return primarySamples;
  }

  override getPrimarySamples(
    structuredTable: StructuredTable<InventoryTableMetadata, InventoryTableRowMetadata>,
    computedTable?: Array<Array<EvaluatedCell>>
  ): {[rowId: string]: Array<PrimarySample>} {
    const primarySamplesByRowId: {[rowId: string]: Array<PrimarySample>} = {};

    structuredTable.rows.forEach(({rowId}, rowIndex) => {
      primarySamplesByRowId[rowId] = [];
      primarySamplesByRowId[rowId] = this.getPrimarySamplesForRow({
        structuredTable,
        computedTable,
        rowIndex,
      });
    });
    return primarySamplesByRowId;
  }

  override getTableKindRowUpdater(): typeof InventoryTableRowUpdater {
    return InventoryTableRowUpdater;
  }

  override allowMergedCellRangeInColumn(
    structuredTable: InventoryStructuredTable,
    column: StructuredTableColumn
  ): boolean {
    // Postion, Container, and Quantity columns are all disabled for Inventory Tables and should then
    // not be mergeable
    const isReadOnlyContainerInfoColumn =
      [TableColumnType.POSITION, TableColumnType.CONTAINERS].includes(column.columnType) &&
      column.columnGroup == null;
    const isReadOnlyQuantityColumn = column.columnGroup === StructuredTableColumnGroups.QUANTITY;
    return !isReadOnlyContainerInfoColumn && !isReadOnlyQuantityColumn;
  }

  override skipUnsubmittedIndicatorsForRow(
    structuredTable: InventoryStructuredTable,
    row: StructuredTableRow<InventoryTableRowMetadata>
  ): boolean {
    if (!structuredTable.metadata.unlockRowsAfterSubmit) {
      return row.isFinalized;
    }
    return false;
  }

  override getDomainGraphTypes(
    schemas: Array<SchemaAndColumnGroup>,
    metadata: InventoryTableMetadata
  ): Array<StructuredTableDomainGraphType> {
    // When doing the 'Update container or plate well attributes' action with any container or any plate,
    // schemas is an empty list. Instead, we use the metadata to determine the core object type.
    switch (metadata.storableSchemaType) {
      case InventoryStructuredTableSchemaType.CONTAINER:
        return [
          {
            objectSpec: {objectType: DomainGraphType.CONTAINER, attributes: {isFixedPlateWell: false}},
            columnType: TableColumnType.CONTAINERS,
            columnGroup: null,
          },
        ];
      case InventoryStructuredTableSchemaType.PLATE:
        return [
          {
            objectSpec: {objectType: DomainGraphType.CONTAINER, attributes: {isFixedPlateWell: true}},
            // It's not possible to select a plate well based on the position column alone - it's also
            // necessary to select a plate in the plate column.
            // This is a placeholder for now - for core object type specific columns to depend on this
            // for custom eval args, columnType may need to be refactored to an array.
            columnType: TableColumnType.POSITION,
            columnGroup: null,
          },
        ];
      default:
        assert(false, `Unexpected schema type ${assertNever(metadata.storableSchemaType)}`);
    }
  }

  override getAllRowUpdaters(): Array<StructuredTableRowUpdater> {
    return super.getAllRowUpdaters().map((rowUpdater) => ({
      ...rowUpdater,
      updateRow(
        model: StructuredTableRowUpdaterModel | InaccessibleInfoGraphQL,
        columns: Array<StructuredTableColumn>,
        row: StructuredTableRow<InventoryTableRowMetadata>,
        columnGroup: ColumnGroup
      ): StructuredTableRow {
        // Unless unlockRowAfterSubmit is true, the inventory table serves as a system of record and only allows
        // each row to be submitted once. For subsequent updates to a container, a new row must be added. This
        // means that finalized rows cannot be refreshed and do not show staleness indicators.
        // When unlockRowAfterSubmit is true, then the row can be resubmitted & should show staleness indicators.
        //
        // This makes sure this logic is consistent across all row updaters, even those associated with core
        // object type columns. If this behavior is desired across other structured tables, it may make sense
        // to expose this as a configuration method on StructuredTableAdapter.
        if (row.isFinalized && !row.metadata.unlockRowAfterSubmit) {
          return row;
        }
        return rowUpdater.updateRow(model, columns, row, columnGroup);
      },
    }));
  }

  override enableCustomColumns(structuredTable: InventoryStructuredTable): boolean {
    return true;
  }

  override enableLookupColumns(structuredTable: InventoryStructuredTable): boolean {
    return true;
  }
}

export type GenericInventoryStructuredTable<TIds> = BaseStructuredTable<
  InventoryTableMetadata,
  InventoryTableRowMetadata,
  TIds
>;
export type InventoryStructuredTable = GenericInventoryStructuredTable<ClientId>;
